import { useState } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import FollowUpReasonSheet from '@/components/FollowUp/Paths/FollowUpReasonSheet';
import CompleteCareTaskSheet from '@/components/FollowUp/CompleteCareTaskSheet';
import { Mutation } from '@/utils/react-query/Mutations';
import { parseTimeFrame } from '@/utils/dater';
import {
    getStepActionIcon,
    getStepActionLabel,
    getStepStatusMeta,
    stepActionIsHuman,
} from '@/utils/constants/followUp';

/**
 * EnrollmentStepCard — one row of a flat Path enrollment timeline (§25.5).
 * Truthful outcome semantics that must hold:
 *
 * - `performerName` is the ACTUAL performer of recorded work, never the
 *   task's first assignee.
 * - Automatic steps report their transport state only (Queued/Accepted/
 *   Skipped/Needs attention) — never "Delivered" or "Connected".
 * - Attempts (no answer/voicemail/sent-no-reply) resolve outreach without
 *   being labeled a connection.
 * - A NEEDS_ATTENTION step offers manager SKIP/RETRY only — RETRY re-arms
 *   the step, it is not a blind resend of an in-flight send.
 */
const EnrollmentStepCard = ({
    organizationID,
    enrollmentID,
    personID,
    personName,
    step,
    index,
    enrollmentActive,
    canManage,
}) => {
    const [completeOpen, setCompleteOpen] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [assignee, setAssignee] = useState(null);
    const [skipOpen, setSkipOpen] = useState(false);

    const status = step?.status || 'PENDING';
    const meta = getStepStatusMeta(status);
    const actionType = step?.actionType;
    const human = stepActionIsHuman(actionType);
    const isOutreach = actionType === 'OUTREACH';
    const title = `${step?.label ?? ''}`.trim() || getStepActionLabel(step);
    const assignees = (step?.assignees || []).filter((a) => a?.assignmentResponse !== 'DECLINED');
    const assigneeNames = assignees.map((a) => a.name).filter(Boolean).join(', ');

    const isCurrent = ['WAITING', 'ACTIVE', 'NEEDS_ATTENTION'].includes(status);
    const isFinished = ['SUCCEEDED', 'EXPIRED', 'SKIPPED', 'CANCELLED', 'RETIRED'].includes(status);
    const hasOpenTask = human && !!step?.taskInstanceID && status === 'ACTIVE';
    const canResolve = canManage && enrollmentActive && ['WAITING', 'ACTIVE', 'NEEDS_ATTENTION'].includes(status);
    const canRetry = canManage && enrollmentActive && status === 'NEEDS_ATTENTION';

    const resolveMutation = Mutation.followUp.resolveStep({
        onSuccess: (data, variables) => {
            Toast.show({
                type: 'success',
                text2: variables?.action === 'RETRY'
                    ? 'Step queued to run again'
                    : 'Step skipped — the Path continues',
            });
            setSkipOpen(false);
        },
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to update the step.' });
        },
    });

    const assignMutation = Mutation.followUp.assignTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Task reassigned' });
            setReassignOpen(false);
            setAssignee(null);
        },
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to reassign the task.' });
        },
    });

    const timingLine = (() => {
        if (status === 'WAITING' && step?.availableAt) {
            return `Starts ${parseTimeFrame(step.availableAt)}`;
        }
        if (status === 'ACTIVE' && human) {
            return step?.expiresAt
                ? `Expires ${parseTimeFrame(step.expiresAt)}`
                : 'No time limit';
        }
        if (isFinished && step?.resolvedAt) {
            return `${meta.label} ${parseTimeFrame(step.resolvedAt)}`;
        }
        if (status === 'PENDING') {
            return 'Runs after the previous step';
        }
        return null;
    })();

    const outcomeLine = (() => {
        if (status === 'SUCCEEDED') {
            const parts = [];
            // outcomeReason carries the truthful transport/result detail
            // (e.g. queue acceptance) — surface it verbatim.
            if (step?.outcomeReason) parts.push(step.outcomeReason);
            if (step?.performerName) parts.push(`by ${step.performerName}`);
            if (step?.resolvedAt) parts.push(parseTimeFrame(step.resolvedAt));
            return parts.filter(Boolean).join(' · ') || 'Done';
        }
        if (status === 'EXPIRED') {
            return 'Expired — the task was cancelled and the Path continued';
        }
        if (status === 'SKIPPED') {
            return step?.skipReason ? `Skipped — ${step.skipReason}` : 'Skipped';
        }
        if (status === 'CANCELLED') {
            return 'Cancelled when the Path stopped';
        }
        if (status === 'NEEDS_ATTENTION' && step?.attentionReason) {
            return step.attentionReason;
        }
        return null;
    })();

    const detailLines = [];
    if (!human && (status === 'ACTIVE' || status === 'WAITING')) {
        detailLines.push(actionType === 'SEND_EMAIL'
            ? 'Queued to send the automatic email.'
            : 'Queued to send the automatic text.');
    }
    if (human && status === 'ACTIVE' && assigneeNames) {
        detailLines.push(`Assigned to ${assigneeNames}`);
    }
    if (human && !assigneeNames && status === 'ACTIVE') {
        detailLines.push('Needs an assignee');
    }

    return (
        <View style={[styles.card, isCurrent && styles.cardCurrent]} testID={`enrollment-step-${step?.stepProgressID}`}>
            <View style={styles.headerRow}>
                <View style={[styles.orderBadge, isFinished && status === 'SUCCEEDED' && styles.orderBadgeDone]}>
                    {status === 'SUCCEEDED'
                        ? <Unicon name="checkmark" size={13} colorVariant="success" />
                        : <Text style={styles.orderText}>{index + 1}</Text>}
                </View>
                <View style={styles.headerBody}>
                    <View style={styles.titleRow}>
                        <Unicon name={getStepActionIcon(step)} size={14} colorVariant="paths" />
                        <Text style={styles.title} numberOfLines={2}>{title}</Text>
                    </View>
                    <View style={styles.chipsRow}>
                        <Chip icon={meta.icon} colorVariant={meta.colorVariant}>{meta.label}</Chip>
                        {!human && (
                            <Chip icon="flash-outline" colorVariant="textSecondary">Automatic</Chip>
                        )}
                        {!!step?.stepArchived && (
                            <Chip icon="eye-off-outline" colorVariant="textTertiary">Removed from template</Chip>
                        )}
                    </View>
                    {!!timingLine && <Text style={styles.metaLine}>{timingLine}</Text>}
                    {!!step?.instructions && status !== 'PENDING' && (
                        <Text style={styles.instructions} numberOfLines={3}>{step.instructions}</Text>
                    )}
                    {detailLines.map((line) => (
                        <Text key={line} style={styles.metaLine}>{line}</Text>
                    ))}
                    {!!outcomeLine && (
                        <Text
                            style={[styles.outcomeLine, status === 'NEEDS_ATTENTION' && styles.attentionLine]}
                            testID={`enrollment-step-outcome-${step?.stepProgressID}`}
                        >
                            {outcomeLine}
                        </Text>
                    )}
                </View>
            </View>

            {/* One primary action for the current human step — Log result /
                Complete task opens the shared structured completion sheet so
                Tasks, Dashboard, and this card resolve work identically. */}
            {enrollmentActive && hasOpenTask && (
                <View style={styles.actionsRow}>
                    <SolidButton
                        testID={`step-complete-${step?.stepProgressID}`}
                        size="sm"
                        iconName={isOutreach ? 'chatbubble-ellipses-outline' : 'checkmark-outline'}
                        text={isOutreach ? 'Log result' : 'Complete task'}
                        onPress={() => setCompleteOpen(true)}
                    />
                    {canManage && (
                        <SolidButton
                            testID={`step-reassign-${step?.stepProgressID}`}
                            size="sm"
                            variant="outlined"
                            type="secondary"
                            iconName="swap-horizontal-outline"
                            text="Reassign"
                            onPress={() => setReassignOpen(true)}
                        />
                    )}
                </View>
            )}

            {(canResolve || canRetry) && (
                <View style={styles.actionsRow}>
                    {canRetry && (
                        <SolidButton
                            testID={`step-retry-${step?.stepProgressID}`}
                            size="sm"
                            variant="outlined"
                            type="secondary"
                            iconName="refresh-outline"
                            text="Retry"
                            disabled={resolveMutation.isPending}
                            onPress={() => resolveMutation.mutate({
                                organizationID,
                                enrollmentID,
                                personID,
                                stepProgressID: step.stepProgressID,
                                action: 'RETRY',
                            })}
                        />
                    )}
                    {canResolve && (
                        <SolidButton
                            testID={`step-skip-${step?.stepProgressID}`}
                            size="sm"
                            variant="outlined"
                            type="textSecondary"
                            iconName="play-skip-forward-outline"
                            text="Skip step"
                            onPress={() => setSkipOpen(true)}
                        />
                    )}
                </View>
            )}

            <CompleteCareTaskSheet
                isOpen={completeOpen}
                onClose={() => setCompleteOpen(false)}
                organizationID={organizationID}
                taskInstanceID={step?.taskInstanceID}
                personID={personID}
                personName={personName}
                purpose={isOutreach ? 'OUTREACH' : 'OTHER'}
                enrollmentID={enrollmentID}
            />

            {reassignOpen && (
                <View style={styles.reassignWrap} testID={`step-reassign-picker-${step?.stepProgressID}`}>
                    <SheetSelector
                        type="people"
                        title="Reassign to"
                        placeholder="Select a person"
                        value={assignee}
                        onChange={(p) => setAssignee(p && !Array.isArray(p) ? p : null)}
                        testID="step-reassign-person"
                    />
                    <View style={styles.actionsRow}>
                        <SolidButton
                            testID="step-reassign-confirm"
                            size="sm"
                            text="Reassign"
                            disabled={!assignee?.id || assignMutation.isPending}
                            onPress={() => assignMutation.mutate({
                                organizationID,
                                taskInstanceID: step.taskInstanceID,
                                assigneePersonID: assignee.id,
                                personID,
                            })}
                        />
                        <SolidButton
                            testID="step-reassign-cancel"
                            size="sm"
                            variant="outlined"
                            type="textSecondary"
                            text="Cancel"
                            onPress={() => { setReassignOpen(false); setAssignee(null); }}
                        />
                    </View>
                </View>
            )}

            <FollowUpReasonSheet
                isOpen={skipOpen}
                onClose={() => setSkipOpen(false)}
                title={`Skip "${title}"`}
                message="Skipping marks this step as intentionally not done and the Path continues to the next step."
                reasonLabel="Skip reason"
                reasonRequired={false}
                confirmText="Skip Step"
                loading={resolveMutation.isPending}
                testIDPrefix="step-skip-sheet"
                onConfirm={(reason) => {
                    resolveMutation.mutate({
                        organizationID,
                        enrollmentID,
                        personID,
                        stepProgressID: step.stepProgressID,
                        action: 'SKIP',
                        reason: reason || null,
                    });
                }}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
        gap: 8,
        boxShadow: shadows.card,
    },
    cardCurrent: {
        borderWidth: 1,
        borderColor: colors.pathsContainer,
    },
    headerRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
    orderBadge: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    orderBadgeDone: { backgroundColor: colors.successContainer },
    orderText: { fontSize: 13, fontFamily: fonts.bold, color: colors.paths },
    headerBody: { flex: 1, minWidth: 0, gap: 4 },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    title: { flex: 1, fontSize: 15, fontFamily: fonts.heading, color: colors.text, minWidth: 0 },
    chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    metaLine: { fontSize: 12, color: colors.textSecondary },
    instructions: { fontSize: 13, color: colors.text, lineHeight: 18 },
    outcomeLine: { fontSize: 12, color: colors.success, lineHeight: 16 },
    attentionLine: { color: colors.warning },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    reassignWrap: { gap: 8 },
}));

export default EnrollmentStepCard;
